import React, { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { ENTER_KEY_CODE } from 'site-modules/shared/constants/key-codes';
import { CANVAS_CREATIVE_ID } from 'site-modules/canvas/constants/canvas';
import { AutosizedArea } from 'site-modules/shared/components/inventory/autosized-area/autosized-area';
import { ChatSurvey } from 'site-modules/canvas/components/chat-survey/chat-survey';
import { useChatInputHandlers } from 'site-modules/shared/hooks/use-chat-input-handlers';
import { MicButton } from 'site-modules/shared/components/mic-button/mic-button';

import './chat-input.scss';

const INPUT_PLACEHOLDER = 'Ask me anything';
const INPUT_MIN_HEIGHT_SM = 44;
const INPUT_MIN_HEIGHT_LG = 106;
const INPUT_HEIGHT_DELTA = 2;

export function ChatInput({
  className = null,
  userMessage = null,
  isLoading = false,
  onSubmit,
  onCancel,
  inputRef,
  withInfoText = true,
  isStartScreen = false,
  isChatInProgress = false,
  showSurvey = false,
  onShowSurvey,
  isMobile = false,
  isAuthenticated = false,
}) {
  const [inputValue, setInputValue] = useState(userMessage);
  const isAuthPending = isAuthenticated === null;
  const isButtonEnabled = isLoading || (!!inputValue?.length && !isAuthPending);

  const {
    isListening,
    isSupported,
    isAllowed,
    stopListening,
    handleInputChange,
    handleInputClick,
    handleToggleListening,
  } = useChatInputHandlers({
    inputRef,
    inputValue,
    onInputChange: setInputValue,
  });

  useEffect(() => {
    startTransition(() => setInputValue(userMessage));
  }, [userMessage]);

  useEffect(() => {
    if (!isChatInProgress) {
      onShowSurvey(false);
    }
  }, [isChatInProgress, onShowSurvey]);

  const submitValue = useCallback(() => {
    if (!inputValue?.trim?.()?.length) {
      return;
    }

    onSubmit(inputValue);
    onShowSurvey(true);
    setInputValue('');
    stopListening();
  }, [inputValue, onShowSurvey, onSubmit, stopListening]);

  const onKeyDown = useCallback(
    event => {
      if (event.keyCode !== ENTER_KEY_CODE || event.shiftKey) {
        return;
      }

      event.preventDefault();
      if (!isLoading && !isAuthPending) {
        submitValue();
      }
    },
    [isAuthPending, isLoading, submitValue]
  );

  const onFocus = useCallback(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_START,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_name: TrackingConstant.VIEW_SEARCH_RESULT,
        subaction_name: TrackingConstant.SELECT_AUTOCOMPLETE,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        creative_id: CANVAS_CREATIVE_ID,
        value: INPUT_PLACEHOLDER,
      },
    });
  }, []);

  const [isMultiLine, setIsMultiLine] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    const el = inputRef?.current;
    if (!el) return () => {};

    observerRef.current = new ResizeObserver(entries => {
      entries.forEach(entry => {
        setIsMultiLine(entry.target.scrollHeight > INPUT_MIN_HEIGHT_SM + INPUT_HEIGHT_DELTA);
      });
    });
    observerRef.current.observe(el);

    return () => observerRef.current?.disconnect();
  }, [inputRef]);

  return (
    <div
      className={classnames('chat-input-wrapper pos-r', className, {
        'pt-0_5': isStartScreen && isMobile,
        'd-flex align-items-center flex-column': !isMobile,
      })}
    >
      {showSurvey && <ChatSurvey />}
      <div
        className={classnames('pos-r mb-0_5', {
          'chat-input-container': isStartScreen,
          'w-100 desktop': !isMobile,
        })}
      >
        <AutosizedArea
          className={classnames('chat-input text-cool-gray-10 size-16 pos-r', {
            'bg-cool-gray-90': inputValue?.length,
            'start-screen overflow-hidden': isStartScreen,
            'w-100': !isMobile,
            'is-multi-line': isMultiLine,
          })}
          innerInputRef={inputRef}
          onChange={handleInputChange}
          onFocus={onFocus}
          onClick={handleInputClick}
          onKeyDown={onKeyDown}
          hintText={INPUT_PLACEHOLDER}
          inputValue={inputValue}
          inputMinHeight={isMobile && !isStartScreen ? INPUT_MIN_HEIGHT_SM : INPUT_MIN_HEIGHT_LG}
          maxLength={5000}
          additionalScrollHeight={!isMobile ? 0 : 2}
          aria-label={INPUT_PLACEHOLDER}
        />
        <div className="tool-buttons pos-a right-0 me-0_25 d-flex align-items-center gap-0_5">
          <MicButton
            isListening={isListening}
            isDisabled={!isAllowed || !isSupported}
            onClick={handleToggleListening}
          />
          <Button
            color="link"
            onClick={isLoading ? onCancel : submitValue}
            className={classnames('submit-btn p-0 d-flex align-items-center justify-content-center', {
              'bg-cool-gray-60': !isButtonEnabled,
              'bg-cool-gray-20': isButtonEnabled,
            })}
            disabled={!isButtonEnabled}
          >
            <span className="visually-hidden">Submit</span>
            <i
              className={classnames({
                'icon-arrow-up8 text-white': !isLoading,
                'icon-checkbox-unchecked3 rounded bg-white text-cool-gray-20': isLoading,
              })}
              aria-hidden
            />
          </Button>
        </div>
      </div>
      {withInfoText && (
        <p
          className={classnames('small text-center', {
            'text-cool-gray-10 mb-0': !!isMobile,
            'text-cool-gray-40 mb-0_5 mt-0_75': !isMobile,
          })}
        >
          Co-Driver AI can make mistakes. Check important info. Do not include personal or sensitive information.{' '}
        </p>
      )}
    </div>
  );
}

ChatInput.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  className: PropTypes.string,
  userMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  inputRef: PropTypes.shape({}).isRequired,
  withInfoText: PropTypes.bool,
  isStartScreen: PropTypes.bool,
  isChatInProgress: PropTypes.bool,
  showSurvey: PropTypes.bool,
  onShowSurvey: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
};
